import { motion } from "framer-motion";
import styled, { css, keyframes } from "styled-components";

/*
 * Novost detalj, v2 retheme na tokene: čiste kartice (--bg-surface/--border),
 * Archivo Black naslov, tamne pilule, ghost akcije, bez gradijenata,
 * glow-ova i shine efekata.
 */

// Keyframes (izvoze se, zadržani, umireni)
export const slideInDown = keyframes`
  from { transform: translateY(-24px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

export const slideInUp = keyframes`
  from { transform: translateY(24px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

export const fadeInUp = keyframes`
  from { transform: translateY(18px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

export const shimmer = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(200%); }
`;

export const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.02); }
`;

export const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-6px); }
`;

export const glow = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.8; }
`;

export const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  100% { background-position: 0% 50%; }
`;

export const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  width: 100%;
  position: relative;
  z-index: 1;
  padding: 0 1.5rem 5rem;

  @media (max-width: 768px) {
    padding: 0 1rem 4rem;
  }
`;

// Navigation
export const BackNavigation = styled.div`
  display: flex;
  align-items: center;
  animation: ${fadeInUp} 0.4s ease-out;
`;

export const BackLink = styled.div`
  z-index: 100;
  display: inline-flex;
  margin: 1.25rem 0;

  /* "← Nazad" samo mobilno (v2 s.10.1) */
  &.mobile-only {
    display: none;

    @media (max-width: 768px) {
      display: inline-flex;
    }
  }
  align-items: center;
  cursor: pointer;
  gap: 0.5rem;
  color: var(--text-primary);
  font-weight: 600;
  font-size: 0.9rem;
  height: 40px;
  padding: 0 1rem;
  border-radius: var(--radius-control);
  background: transparent;
  border: 1px solid var(--border-strong);
  text-decoration: none;
  width: fit-content;
  transition: all 0.18s ease;

  svg {
    color: var(--brand);
  }

  &:hover {
    border-color: var(--brand);
    color: var(--brand);
  }
`;

export const Breadcrumbs = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 1rem;
  color: var(--text-secondary);
  font-size: 0.88rem;

  /* breadcrumb iznad heroja (v2 s.10.1), desktop only */
  &.top {
    margin: 1.25rem 0 1.5rem;
    flex-wrap: wrap;

    @media (max-width: 768px) {
      display: none;
    }
  }

  a {
    color: var(--text-secondary);
    text-decoration: none;

    &:hover {
      color: var(--text-primary);
    }
  }

  span {
    color: var(--text-secondary);
  }

  .current {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 380px;
  }

  .separator {
    color: var(--text-muted);
  }

  .current {
    color: var(--text-primary);
    font-weight: 600;
  }
`;

// Article Header, hero cover
export const ArticleHeader = styled.div`
  border-radius: var(--radius-card);
  padding: 3rem;
  margin-bottom: 2rem;
  /* border: 1px solid var(--border); */
  position: relative;
  overflow: hidden;
  animation: ${slideInDown} 0.5s ease-out;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  /* Slika je sadržaj, gradijent SAMO pri dnu radi čitljivosti (v2 s.10.1) */
  background: ${({ $backgroundImage }) =>
    $backgroundImage
      ? `linear-gradient(
          180deg,
          transparent 55%,
          rgba(19, 16, 16, 0.9) 100%
        ),
        url(${$backgroundImage})`
      : "var(--bg-surface)"};

  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  min-height: ${({ $backgroundImage }) => ($backgroundImage ? "50vh" : "auto")};

  display: flex;
  flex-direction: column;
  justify-content: flex-end;

  > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

export const ArticleCategory = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(0, 0, 0, 0.6);
  color: var(--text-primary);
  padding: 0.3rem 0.75rem;
  border-radius: var(--radius-pill);
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 1.25rem;
  width: fit-content;
  animation: ${fadeInUp} 0.5s ease-out 0.2s both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const ArticleTitle = styled.h1`
  color: var(--text-primary);
  font-family: var(--font-display);
  font-size: clamp(30px, 4.5vw, 54px);
  line-height: 1;
  letter-spacing: -0.01em;
  text-transform: uppercase;
  margin-bottom: 1.25rem;
  animation: ${fadeInUp} 0.55s ease-out 0.3s both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const ArticleMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  align-items: center;
  color: var(--text-secondary);
  font-size: 0.9rem;

  /* meta pilule ulete jedna za drugom posle naslova */
  & > * {
    animation: ${fadeInUp} 0.45s ease-out backwards;
  }

  ${Array.from(
    { length: 5 },
    (_, i) => css`
      & > *:nth-child(${i + 1}) {
        animation-delay: ${420 + i * 70}ms;
      }
    `,
  )}

  @media (prefers-reduced-motion: reduce) {
    & > * {
      animation: none;
    }
  }

  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`;

export const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(0, 0, 0, 0.45);
  padding: 0.35rem 0.75rem;
  border-radius: var(--radius-pill);
  border: 1px solid var(--border-strong);
  font-size: 0.85rem;

  svg {
    color: var(--brand);
  }
`;

export const BandTag = styled.span`
  background: rgba(0, 0, 0, 0.45);
  color: var(--brand);
  padding: 0.35rem 0.75rem;
  border-radius: var(--radius-pill);
  border: 1px solid var(--border-strong);
  font-size: 0.85rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

// Article Content
export const ArticleMain = styled.div`
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 1.5rem;
  margin-bottom: 3rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

export const ArticleContent = styled.div`
  background: var(--bg-surface);
  border-radius: var(--radius-card);
  padding: 2.5rem;
  border: 1px solid var(--border);
  animation: ${slideInUp} 0.5s ease-out 0.1s both;
  height: fit-content;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

export const ArticleText = styled.div`
  color: var(--text-secondary);
  font-size: 1.02rem;
  line-height: 1.75;

  p {
    margin-bottom: 1.25rem;
  }

  h2,
  h3 {
    color: var(--text-primary);
    margin: 2rem 0 1rem 0;
    font-weight: 700;
  }

  h2 {
    font-size: 1.5rem;
  }

  h3 {
    font-size: 1.2rem;
  }

  a {
    color: var(--brand);
  }

  img {
    max-width: 100%;
    border-radius: var(--radius-control);
  }

  blockquote {
    background: var(--brand-dim);
    border-left: 3px solid var(--brand);
    padding: 1.25rem;
    margin: 1.5rem 0;
    border-radius: 0 var(--radius-control) var(--radius-control) 0;
    font-style: italic;
    color: var(--text-primary);
  }

  ul,
  ol {
    padding-left: 1.5rem;
    margin-bottom: 1.25rem;
  }

  li {
    margin-bottom: 0.5rem;
  }
`;

// Empty State
export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
  border-radius: var(--radius-card);
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid var(--border);
`;

export const EmptyStateIcon = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--brand-dim);
  border-radius: 50%;
  margin-bottom: 1rem;

  svg {
    color: var(--brand);
    font-size: 1.5rem;
  }
`;

export const EmptyStateText = styled.p`
  color: var(--text-muted);
  font-size: 0.92rem;
  margin: 0;
  line-height: 1.4;
  max-width: 250px;
`;

// Sidebar
export const ArticleSidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  /* sidebar kartice ulaze jedna za drugom, posle sadržaja */
  & > * {
    animation: ${slideInUp} 0.5s ease-out backwards;
  }

  & > *:nth-child(1) {
    animation-delay: 0.25s;
  }

  & > *:nth-child(2) {
    animation-delay: 0.35s;
  }

  @media (prefers-reduced-motion: reduce) {
    & > * {
      animation: none;
    }
  }
`;

export const SidebarCard = styled.div`
  background: var(--bg-surface);
  border-radius: var(--radius-card);
  padding: 1.5rem;
  border: 1px solid var(--border);
  transition: border-color 0.2s ease;

  &:hover {
    border-color: var(--border-strong);
  }
`;

export const SidebarTitle = styled.h3`
  color: var(--text-muted);
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  margin-bottom: 1.25rem;
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    color: var(--brand);
  }
`;

// Social Share
export const SocialShare = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

export const ShareButton = styled.button`
  background: transparent;
  color: var(--text-secondary);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-control);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.18s ease;
  min-width: 42px;
  height: 42px;

  &:hover {
    color: var(--brand);
    border-color: var(--brand);
  }
`;

export const RelatedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

// Related Articles, reveal na scroll ($visible iz useReveal)
export const RelatedArticles = styled.div`
  margin-top: 4rem;
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  transform: translateY(${(p) => (p.$visible ? "0" : "26px")});
  transition:
    opacity 0.6s ease,
    transform 0.6s cubic-bezier(0.2, 0.8, 0.2, 1);

  /* kartice ulete stepenasto kad sekcija postane vidljiva */
  ${(p) =>
    p.$visible &&
    css`
      ${RelatedGrid} > * {
        animation: ${fadeInUp} 0.5s ease-out backwards;
      }

      ${Array.from(
        { length: 3 },
        (_, i) => css`
          ${RelatedGrid} > *:nth-child(${i + 1}) {
            animation-delay: ${150 + i * 90}ms;
          }
        `,
      )}
    `}

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    transform: none;
    transition: none;

    ${RelatedGrid} > * {
      animation: none;
    }
  }
`;

export const RelatedTitle = styled.h2`
  color: var(--text-primary);
  font-family: var(--font-display);
  font-size: clamp(24px, 3vw, 36px);
  line-height: 1;
  letter-spacing: -0.01em;
  text-transform: uppercase;
  margin-bottom: 1.75rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  svg {
    color: var(--brand);
  }
`;

export const RelatedCard = styled(motion.article)`
  background: var(--bg-surface);
  border-radius: var(--radius-card);
  overflow: hidden;
  border: 1px solid var(--border);
  transition:
    border-color 0.2s ease,
    transform 0.2s ease;
  cursor: pointer;

  &:hover {
    border-color: var(--border-strong);
    transform: translateY(-2px);
  }
`;

export const RelatedImage = styled.div`
  height: 150px;
  background: var(--bg-elevated);
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const RelatedContent = styled.div`
  padding: 1.25rem;
`;

export const RelatedCardTitle = styled.h3`
  color: var(--text-primary);
  font-size: 1.05rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  line-height: 1.35;
`;

export const RelatedExcerpt = styled.p`
  color: var(--text-secondary);
  font-size: 0.9rem;
  line-height: 1.5;
  margin-bottom: 1rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

// Tags
export const ArticleTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 2rem;
`;

export const Tag = styled.span`
  background: transparent;
  color: var(--text-secondary);
  padding: 0.3rem 0.75rem;
  border-radius: var(--radius-pill);
  font-size: 0.8rem;
  font-weight: 600;
  border: 1px solid var(--border-strong);
  transition: all 0.18s ease;

  &:hover {
    color: var(--brand);
    border-color: var(--brand);
  }
`;

// Action Buttons
export const ActionButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--border);
`;

export const ActionBtn = styled.button`
  background: ${({ $primary }) => ($primary ? "var(--brand)" : "transparent")};
  color: ${({ $primary }) => ($primary ? "#ffffff" : "var(--text-primary)")};
  border: 1px solid
    ${({ $primary }) => ($primary ? "var(--brand)" : "var(--border-strong)")};
  height: 44px;
  padding: 0 1.2rem;
  border-radius: var(--radius-control);
  cursor: pointer;
  transition: all 0.18s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 600;
  font-size: 0.92rem;

  &:hover {
    ${({ $primary }) =>
      $primary
        ? "background: var(--brand-hover);"
        : "border-color: var(--brand); color: var(--brand);"}
    transform: translateY(-1px);
  }
`;
